import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { hasArtistRosterAccess, hasBulkUploadAccess, hasEnterprisePortalAccess, hasTeamAccess, hasVideoDistributionAccess } from '../lib/entitlements';
import { useEffect, useState } from 'react';

const ADMIN_HOST_ROLES = new Set([
  'owner',
  'coo',
  'cao',
  'aceo',
  'acoo',
  'cmo',
  'cto',
  'cfo',
  'cro',
  'cco',
  'admin',
  'qc_officer',
  'support_agent',
]);

export function getAdminLandingPath(user) {
  if (!user) return '/login';
  if (!ADMIN_HOST_ROLES.has(user.role) && Number(user.power_level || 0) >= 8) {
    return '/admin/boardroom';
  }
  return '/admin/staff-menu';
}

export function getAdminHomeLabel() {
  return 'The Record Base';
}

export function hasAdminHostAccess(user) {
  const hasCustomAdminAccess = user?.custom_admin_permissions_enabled
    && user.role !== 'owner'
    && Array.isArray(user.admin_permissions)
    && user.admin_permissions.some((permission) => Object.values(adminPanelPermissionKeys).includes(permission));
  return Boolean(user) && (
    ADMIN_HOST_ROLES.has(user.role) ||
    Number(user.power_level || 0) >= 8 ||
    hasCustomAdminAccess
  );
}

export function ProtectedRoute({ children, requiredRoles, requiredAccess }) {
  const { user, loading, refreshUser } = useAuth();
  const location = useLocation();
  const [bulkAccessRefreshStatus, setBulkAccessRefreshStatus] = useState('idle');
  const isAdminHost = typeof window !== 'undefined' && window.location.hostname.toLowerCase().startsWith('admin.');

  useEffect(() => {
    if (
      loading ||
      requiredAccess !== 'bulk_upload' ||
      !user ||
      user === false ||
      hasBulkUploadAccess(user) ||
      bulkAccessRefreshStatus !== 'idle'
    ) {
      return;
    }
    let active = true;
    const timeout = window.setTimeout(() => {
      if (active) setBulkAccessRefreshStatus('done');
    }, 4000);
    setBulkAccessRefreshStatus('refreshing');
    refreshUser().finally(() => {
      if (!active) return;
      window.clearTimeout(timeout);
      setBulkAccessRefreshStatus('done');
    });
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [bulkAccessRefreshStatus, loading, refreshUser, requiredAccess, user]);

  if (loading) {
    if (user && user !== false) {
      return children;
    }
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#0070f3] animate-spin" />
      </div>
    );
  }

  if (user === false || user === null) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user.is_demo_account && !location.pathname.startsWith('/demo')) {
    return <Navigate to="/demo" replace />;
  }

  if (user.signup_kyc_required && location.pathname !== '/signup/verify') {
    return <Navigate to="/signup/verify" replace />;
  }

  if (user.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Check if user has completed onboarding
  if (!user.onboarding_complete && 
      !location.pathname.startsWith('/onboarding') && 
      location.pathname !== '/signup/verify' &&
      !location.pathname.startsWith('/cmgadmin') &&
      !hasAdminHostAccess(user)) {
    return <Navigate to="/onboarding/plan" replace />;
  }

  // Check role requirements
  if (requiredRoles && requiredRoles.length > 0) {
    const hasBoardroomPowerOverride = location.pathname === '/admin/boardroom'
      && Number(user.power_level || 0) >= 8;
    if (!userCanAccessAdminRoles(user, requiredRoles) && !hasBoardroomPowerOverride) {
      return <Navigate to={isAdminHost ? (hasAdminHostAccess(user) ? getAdminLandingPath(user) : '/login') : "/dashboard"} replace />;
    }
  }

  if (requiredAccess === 'artist_roster' && !hasArtistRosterAccess(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredAccess === 'team_management' && !hasTeamAccess(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredAccess === 'video_distribution' && !hasVideoDistributionAccess(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredAccess === 'ai_mastering' && !user.ai_mastering_enabled) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredAccess === 'bulk_upload' && !hasBulkUploadAccess(user)) {
    if (bulkAccessRefreshStatus === 'idle' || bulkAccessRefreshStatus === 'refreshing') {
      return (
        <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#0070f3] animate-spin" />
        </div>
      );
    }
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredAccess === 'enterprise_portal' && !hasEnterprisePortalAccess(user)) {
    return <Navigate to="/enterprise/access-denied" replace />;
  }

  return children;
}

export const adminPanelRoles = {
  home: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cmo', 'cto', 'cfo', 'cro', 'cco', 'admin', 'qc_officer', 'support_agent'],
  staffHelper: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cmo', 'cto', 'cfo', 'cro', 'cco', 'admin', 'qc_officer', 'support_agent'],
  staffHelp: ['owner', 'coo', 'cao', 'aceo', 'acoo'],
  boardroom: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cmo', 'cto', 'cfo', 'cro', 'cco', 'admin'],
  presaves: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cro', 'cco', 'admin'],
  queue: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cro', 'cco', 'admin', 'qc_officer'],
  releaseManager: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cro', 'cco', 'admin', 'qc_officer'],
  ddex: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cro', 'admin', 'qc_officer'],
  dummyStore: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'admin'],
  bank: ['owner', 'coo', 'aceo', 'acoo', 'admin', 'cfo'],
  billing: ['owner', 'coo', 'aceo', 'acoo', 'admin', 'cfo', 'support_agent'],
  takedowns: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'admin', 'qc_officer'],
  partners: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'admin'],
  developer: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cto', 'admin'],
  tickets: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'admin', 'support_agent'],
  mailbox: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cto', 'admin', 'support_agent'],
  mailboxAdmin: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cto', 'admin'],
  helpArticles: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'admin', 'support_agent'],
  export: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cro', 'admin', 'qc_officer'],
  specialAccess: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'admin', 'qc_officer'],
  lyrics: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'admin', 'qc_officer'],
  staff: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'admin'],
  siteStaff: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'admin'],
  accountManagers: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'admin', 'cfo', 'support_agent'],
  careers: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'admin'],
  users: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'admin'],
  marketing: ['owner', 'coo', 'cmo', 'aceo', 'acoo', 'admin'],
  calendar: ['owner'],
  ourDesigns: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cmo', 'cto', 'admin'],
  mfeb: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cmo', 'cto', 'admin'],
  mafa: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cmo', 'cto', 'admin'],
  artistAwards: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cmo', 'cro', 'admin'],
  affiliates: ['owner', 'coo', 'cmo', 'aceo', 'acoo', 'admin'],
  news: ['owner', 'coo', 'cmo', 'aceo', 'acoo', 'admin'],
  social: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cmo', 'cro', 'cco', 'admin', 'qc_officer', 'support_agent'],
  roadmap: ['owner', 'coo', 'cmo', 'aceo', 'acoo', 'admin'],
  legal: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'admin'],
  demoAccounts: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'admin', 'cmo'],
  compliance: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'admin', 'cfo'],
  ownerVault: ['owner', 'coo', 'aceo', 'acoo'],
  ownerDocs: ['owner'],
  enterpriseAccess: ['owner', 'coo', 'aceo', 'acoo', 'admin'],
  contentId: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cco', 'admin', 'qc_officer', 'support_agent'],
  acrCloud: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cco', 'admin', 'qc_officer', 'support_agent'],
  needsAttention: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cro', 'cco', 'admin', 'qc_officer', 'support_agent'],
  distroGrid: ['owner', 'coo', 'cao', 'aceo', 'acoo', 'cto', 'cfo', 'cro', 'admin', 'qc_officer', 'support_agent'],
};

export const adminPanelPermissionKeys = {
  staffHelper: 'staff_helper',
  staffHelp: 'staff_help',
  boardroom: 'boardroom',
  presaves: 'presaves',
  queue: 'queue',
  releaseManager: 'release_manager',
  ddex: 'ddex',
  dummyStore: 'dummy_store',
  bank: 'bank',
  billing: 'billing',
  takedowns: 'takedowns',
  partners: 'partners',
  developer: 'developer',
  tickets: 'tickets',
  mailbox: 'mailbox',
  mailboxAdmin: 'mailbox_admin',
  helpArticles: 'help_articles',
  export: 'export',
  specialAccess: 'special_access',
  lyrics: 'lyrics',
  staff: 'staff',
  siteStaff: 'site_staff',
  accountManagers: 'account_managers',
  careers: 'careers',
  users: 'users_admin',
  marketing: 'marketing',
  calendar: 'calendar',
  ourDesigns: 'our_designs',
  mfeb: 'mfeb',
  mafa: 'mafa',
  artistAwards: 'artist_awards',
  affiliates: 'affiliates',
  news: 'news',
  social: 'social',
  roadmap: 'roadmap',
  legal: 'legal',
  demoAccounts: 'demo_accounts',
  compliance: 'compliance',
  ownerVault: 'owner_vault',
  enterpriseAccess: 'enterprise_access',
  contentId: 'content_id',
  acrCloud: 'acrcloud',
  needsAttention: 'needs_attention',
  distroGrid: 'distrogrid_overview',
};

export function getAdminPermissionKeyForRoles(roles) {
  const match = Object.entries(adminPanelRoles).find(([, panelRoles]) => panelRoles === roles);
  return match ? adminPanelPermissionKeys[match[0]] : null;
}

export function userCanAccessAdminRoles(user, roles) {
  if (!user || !Array.isArray(roles)) return false;
  if (user.custom_admin_permissions_enabled && user.role !== 'owner') {
    if (!Array.isArray(user.admin_permissions)) return false;
    if (roles === adminPanelRoles.home) {
      return user.admin_permissions.some((permission) => Object.values(adminPanelPermissionKeys).includes(permission));
    }
    const permissionKey = getAdminPermissionKeyForRoles(roles);
    if (permissionKey === 'staff_helper') return user.staff_helper_enabled !== false;
    return Boolean(permissionKey && user.admin_permissions.includes(permissionKey));
  }
  if (roles.includes(user.role)) return true;
  if (!Array.isArray(user.admin_permissions)) return false;
  const permissionKey = getAdminPermissionKeyForRoles(roles);
  return Boolean(permissionKey && user.admin_permissions.includes(permissionKey));
}

export function AdminRoute({ children, requiredRoles }) {
  const adminRoles = requiredRoles || adminPanelRoles.home;
  return <ProtectedRoute requiredRoles={adminRoles}>{children}</ProtectedRoute>;
}
